import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { get as getUser } from 'js/lib/user';

import {
  GlobalNotificationCategory,
  NotificationPreferenceSource,
} from '@coursera/grpc-types-notifications/coursera/proto/notifications/preference/v1/notification_preference';
import { ChannelType } from '@coursera/grpc-types-notifications/coursera/proto/notifications/shared/v1/channel_type';
import { ContactSource } from '@coursera/grpc-types-notifications/coursera/proto/notifications/shared/v1/contact';

import { useNaptime } from 'bundles/naptimejs';
import ProfileConsentsV1 from 'bundles/naptimejs/resources/profileConsents.v1';
import type { UpdateNotificationPreferencesMutationVariables } from 'bundles/user-consent/queries/UpdateNotificationPreferencesMutation';
import { UpdateNotificationPreferencesMutation } from 'bundles/user-consent/queries/UpdateNotificationPreferencesMutation';

const DEFAULT_LANGUAGE_CODE = 'en';

/**
 * Actions for profile consents related to CCPA and GDPR.
 *
 * @constant
 * @type {Object}
 * @property {string} CCPA - Action for consenting to CCPA.
 * @property {string} GDPR - Action for consenting to GDPR.
 */
export const ProfileConsentActions = {
  CCPA: 'consentToCCPA',
  GDPR: 'consentToGDPR',
} as const;

export type ProfileConsentAction = (typeof ProfileConsentActions)[keyof typeof ProfileConsentActions];

const useUpdateUserNotificationPreferences = (userId: number) => {
  const [updatePreferences] = useMutation<{}, UpdateNotificationPreferencesMutationVariables>(
    UpdateNotificationPreferencesMutation,
    {
      onError: (error) => {
        Sentry.captureException(error, {
          extra: {
            message: `User notification preferences for ${ChannelType.CHANNEL_TYPE_EMAIL} have not been properly updated`,
          },
        });
      },
    }
  );
  const updateNotificationPreferences = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (isSubscribed: boolean) => {
      updatePreferences({
        variables: {
          input: {
            preferences: [
              {
                contact: {
                  contactSource: ContactSource.CONTACT_SOURCE_COURSERA_USER,
                  contactId: userId.toString(),
                },
                preferenceType: {
                  globalPreference: {
                    channel: ChannelType.CHANNEL_TYPE_EMAIL,
                    category: GlobalNotificationCategory.GLOBAL_NOTIFICATION_CATEGORY_ENTERPRISE_WEEKLY_RECOMMENDATION,
                  },
                },
                isSubscribed,
                lastModifiedAt: Date.now(),
                infoSource: NotificationPreferenceSource.NOTIFICATION_PREFERENCE_SOURCE_COURSERA_SETTINGS_PROMPTS,
              },
            ],
          },
        },
      });
    },
    [updatePreferences, userId]
  );

  return updateNotificationPreferences;
};

/**
 * Custom hook to handle accepting marketing agreements.
 *
 * This hook creates a function to accept the marketing agreement based on the user's status (enterprise admin or not)
 * and updates the user notification preferences accordingly. It also handles mutation execution and error capturing.
 *
 * @param {boolean} isEnterpriseAdmin - Indicates whether the user is an enterprise admin.
 * @param {boolean} [isNewUser=false] - Indicates whether the user is new. Defaults to false.
 * @param {number} [userAccountId] - The user account ID. If not provided, the ID of the currently authenticated user is used.
 *
 * @returns {Function} Function to accept the marketing agreement.
 * The function takes two parameters: `consentAction` of type `ProfileConsentAction` and `allowMarketingEmail` of type boolean.
 * The function returns a promise, which resolves to the result of the mutation execution, or rejects with an error if the mutation fails.
 */
const useAcceptMarketingAgreement = (
  isEnterpriseAdmin: boolean,
  isNewUser: boolean = false,
  userAccountId?: number
) => {
  const userId = userAccountId || getUser().id;
  const locale = getUser().locale;

  const naptimeCb = useCallback<() => $TSFixMe>(() => ({}), []);
  const { naptime } = useNaptime(naptimeCb);

  const updateNotificationPreferences = useUpdateUserNotificationPreferences(userId);
  const acceptMarketingAgreement = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (consentAction: ProfileConsentAction, allowMarketingEmail: boolean) => {
      // Subscribe to the Enterprise learner email marketing channel when the user is a Consumer user or Enterprise learner - non Enterprise Admin
      // For more details, see https://docs.google.com/document/d/1-Z-A4Lr0HAz347Zf8zRPnGnouHz_-wEX-6cGw112lN4/edit
      if (!isEnterpriseAdmin) {
        updateNotificationPreferences(allowMarketingEmail);
      }

      try {
        return naptime.executeMutation(
          ProfileConsentsV1.action(
            consentAction,
            {},
            {
              userId,
              consentLanguageCd: locale || DEFAULT_LANGUAGE_CODE,
              consentLanguageVersion: 1,
              allowMarketingEmails: !isEnterpriseAdmin && allowMarketingEmail,
              allowEnterpriseMarketingEmails: isEnterpriseAdmin && allowMarketingEmail,
              ...(consentAction === ProfileConsentActions.CCPA && { isExistingUser: !isNewUser }),
            }
          )
        );
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            consentAction,
            message: `Profile Consent for ${
              consentAction === ProfileConsentActions.CCPA ? 'CCPA' : 'GDPR'
            } has not been properly updated`,
          },
        });
        // Ensure a value is returned
        return Promise.reject(error);
      }
    },
    [isEnterpriseAdmin, updateNotificationPreferences, naptime, userId, locale, isNewUser]
  );

  return acceptMarketingAgreement;
};

export default useAcceptMarketingAgreement;
